/**
 * LetterDrafter — UI für den Auto-Drafted-Letter-Endpoint.
 *
 * Drei sichtbare Phasen:
 *   1. Form (letterType + Sender/Empfänger/Frist + Forderung).
 *   2. Loading-Indikator während draftLetter().
 *   3. Preview (Plaintext-Brief + DOCX-Download + Disclaimers).
 *
 * Die Form bleibt bewusst kurz — Pflichtfelder sind nur Sender/Empfänger/Typ.
 * Der Hauptinhalt kommt aus dem caseContext (vom Dashboard durchgereicht).
 *
 * Sicherheits-/UX-Hinweis:
 *   • Der DOCX-Download läuft im Browser via Blob aus base64-decoded bytes,
 *     ohne weiteren Server-Roundtrip.
 *   • Der Brief-Preview ist <pre> mit whitespace-pre-wrap, damit der Layout-
 *     Eindruck dem späteren DOCX entspricht.
 *   • Die Disclaimer-Box ist BEWUSST nicht versteckbar — der RDG-Hinweis
 *     muss vor dem Senden gelesen worden sein.
 */

import { useEffect, useMemo, useState } from "react";
import {
  draftLetter,
  docxBase64ToBlob,
  listLetterTypes,
  type DraftedLetter,
  type LetterType,
  type LetterTypeInfo,
} from "../lib/api";

/**
 * Sprint I/2 (2026-04-29): Pre-Fill-Payload aus B2CAdvisor.SuggestedLetterCTA.
 * Wird via react-router `Link state` durchgereicht und in LetterPage über
 * useLocation().state ausgelesen.
 *
 * BEWUSST nur drei Felder — Sender-Identität (Name/Anschrift) bleibt User-
 * Eingabe, weil der Mock-Jury-Step diese Daten nie hat. Forderungs-Betrag
 * könnte aus dem Verdict übernommen werden, aber das wäre eine zusätzliche
 * Quelle für falsche Default-Werte; lieber ein leeres Feld als ein falsches.
 */
export interface LetterPrefill {
  letterType: LetterType;
  caseContext: string;
  recipientName?: string;
}

interface Props {
  /** Default-Fallbeschreibung — wird typischerweise vom Dashboard durchgereicht. */
  initialCaseContext?: string;
  /** Sprint I/2: optionaler Pre-Fill aus dem Mock-Jury-Verdict-Flow. */
  initialPrefill?: LetterPrefill;
}

export default function LetterDrafter({ initialCaseContext, initialPrefill }: Props) {
  const [letterType, setLetterType] = useState<LetterType>(
    initialPrefill?.letterType ?? "mahnung"
  );
  const [caseContext, setCaseContext] = useState(
    initialPrefill?.caseContext ??
      initialCaseContext ??
      "Ich habe am 12.03.2026 ein Smartphone (iPhone 15) bei der Beispiel GmbH gekauft (Rechnung Nr. R-2026-0033). Nach 4 Wochen lädt das Gerät nicht mehr zuverlässig. Mehrere Aufforderungen zur Nachbesserung blieben ohne Reaktion."
  );
  const [senderName, setSenderName] = useState("");
  const [senderAddress, setSenderAddress] = useState("");
  const [recipientName, setRecipientName] = useState(
    initialPrefill?.recipientName ?? ""
  );
  const [recipientAddress, setRecipientAddress] = useState("");
  const [referenceNumber, setReferenceNumber] = useState("");
  const [claimAmountEur, setClaimAmountEur] = useState<string>("");
  const [claimDeadlineDays, setClaimDeadlineDays] = useState<string>("");

  const [types, setTypes] = useState<LetterTypeInfo[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [letter, setLetter] = useState<DraftedLetter | null>(null);

  useEffect(() => {
    listLetterTypes()
      .then((r) => setTypes(r.types))
      .catch(() => setTypes(null));
  }, []);

  const canSubmit = useMemo(
    () =>
      caseContext.trim().length >= 20 &&
      senderName.trim().length >= 2 &&
      recipientName.trim().length >= 2,
    [caseContext, senderName, recipientName]
  );

  const onSubmit = async () => {
    setLoading(true);
    setError(null);
    setLetter(null);
    try {
      const result = await draftLetter({
        letterType,
        caseContext,
        senderName,
        senderAddress: senderAddress || undefined,
        recipientName,
        recipientAddress: recipientAddress || undefined,
        referenceNumber: referenceNumber || undefined,
        claimAmountEur: claimAmountEur ? Number(claimAmountEur) : undefined,
        claimDeadlineDays: claimDeadlineDays ? Number(claimDeadlineDays) : undefined,
      });
      setLetter(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  };

  const onDownloadDocx = () => {
    if (!letter) return;
    const blob = docxBase64ToBlob(letter.docxBase64);
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = letter.filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-5">
      <div>
        <label className="block text-sm text-slate-300 mb-1">
          Brief-Typ <span className="text-slate-500">(steuert Paragraphen-Set)</span>
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {(types ?? FALLBACK_TYPES).map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setLetterType(t.id)}
              className={`text-left rounded-lg px-3 py-2 text-sm border transition ${
                letterType === t.id
                  ? "bg-court-700/30 border-court-500 text-court-100"
                  : "bg-slate-900 border-slate-700 text-slate-300 hover:border-slate-600"
              }`}
            >
              <div className="font-medium">{t.label}</div>
              <div className="text-xs text-slate-500">
                Frist Standard: {t.defaultDeadlineDays} Tage
              </div>
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field
          label="Dein Name"
          value={senderName}
          onChange={setSenderName}
          placeholder="Max Mustermann"
          required
        />
        <Field
          label="Deine Anschrift"
          value={senderAddress}
          onChange={setSenderAddress}
          placeholder="Musterstr. 1, 12345 Beispielstadt"
        />
        <Field
          label="Empfänger (Name / Firma)"
          value={recipientName}
          onChange={setRecipientName}
          placeholder="Beispiel GmbH"
          required
        />
        <Field
          label="Empfänger-Anschrift"
          value={recipientAddress}
          onChange={setRecipientAddress}
          placeholder="Industriestr. 99, 67890 Beispielort"
        />
        <Field
          label="Aktenzeichen / Vertrag-Nr."
          value={referenceNumber}
          onChange={setReferenceNumber}
          placeholder="z.B. R-2026-0033"
        />
        <Field
          label="Betrag (EUR, optional)"
          value={claimAmountEur}
          onChange={setClaimAmountEur}
          placeholder="z.B. 920"
          inputType="number"
        />
        <Field
          label="Frist in Tagen (optional)"
          value={claimDeadlineDays}
          onChange={setClaimDeadlineDays}
          placeholder="leer = Standard pro Brieftyp"
          inputType="number"
        />
      </div>

      <div>
        <label className="block text-sm text-slate-300 mb-1">
          Sachverhalt <span className="text-slate-500">(was ist passiert?)</span>
        </label>
        <textarea
          value={caseContext}
          onChange={(e) => setCaseContext(e.target.value)}
          rows={6}
          className="w-full rounded-lg bg-slate-900 border border-slate-700 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-court-500 font-mono"
        />
      </div>

      <button
        onClick={onSubmit}
        disabled={loading || !canSubmit}
        className="w-full rounded-lg bg-gradient-to-r from-court-500 to-court-700 hover:from-court-400 hover:to-court-600 disabled:opacity-50 px-4 py-3 font-semibold transition shadow-lg shadow-court-700/30"
      >
        {loading ? "Brief wird entworfen…" : "📄 Brief generieren (DOCX + Plaintext)"}
      </button>

      {error && (
        <div className="rounded-lg bg-red-950/50 border border-red-800 px-4 py-2 text-sm text-red-200">
          {error}
        </div>
      )}

      {letter && <LetterPreview letter={letter} onDownloadDocx={onDownloadDocx} />}
    </div>
  );
}

const FALLBACK_TYPES: LetterTypeInfo[] = [
  { id: "widerspruch", label: "Widerspruch (Bescheid / Vertrag)", defaultDeadlineDays: 30 },
  { id: "mahnung", label: "Mahnung & Fristsetzung", defaultDeadlineDays: 14 },
  { id: "mietminderung", label: "Mietminderung wegen Mangel", defaultDeadlineDays: 14 },
  { id: "eg261_forderung", label: "EU-Fluggastrechte (EG 261/2004)", defaultDeadlineDays: 14 },
  { id: "gewaehrleistung", label: "Gewährleistung / Mängelrüge", defaultDeadlineDays: 14 },
];

interface FieldProps {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  required?: boolean;
  inputType?: "text" | "number";
}

function Field({ label, value, onChange, placeholder, required, inputType = "text" }: FieldProps) {
  return (
    <label className="block">
      <span className="block text-sm text-slate-300 mb-1">
        {label} {required && <span className="text-red-400">*</span>}
      </span>
      <input
        type={inputType}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full rounded-lg bg-slate-900 border border-slate-700 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-court-500"
      />
    </label>
  );
}

function LetterPreview({
  letter,
  onDownloadDocx,
}: {
  letter: DraftedLetter;
  onDownloadDocx: () => void;
}) {
  return (
    <div className="rounded-2xl border border-court-700/60 bg-slate-900/60 overflow-hidden">
      <div className="p-5 border-b border-slate-800 flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-wider text-slate-400">Entwurf</p>
          <p className="text-lg font-semibold text-slate-100">{letter.subject}</p>
          <p className="text-xs text-slate-500">
            Frist bis <span className="font-mono text-court-300">{letter.deadlineDate}</span> · Typ:{" "}
            {letter.letterType}
          </p>
        </div>
        <button
          type="button"
          onClick={onDownloadDocx}
          className="rounded-lg bg-court-600 hover:bg-court-500 px-4 py-2 text-sm font-semibold shadow-md transition"
        >
          ⬇ DOCX herunterladen ({letter.filename})
        </button>
      </div>

      <div className="p-5">
        <pre className="whitespace-pre-wrap text-sm text-slate-200 font-sans bg-slate-950/70 border border-slate-800 rounded-lg p-4 leading-relaxed max-h-[480px] overflow-auto">
          {letter.plainText}
        </pre>
      </div>

      <div className="px-5 pb-5 space-y-3">
        <div className="rounded-lg border border-amber-800/60 bg-amber-950/30 px-4 py-3 text-xs text-amber-100/90 space-y-1">
          <p className="font-semibold uppercase tracking-wider text-amber-300">
            Wichtige Hinweise
          </p>
          <ul className="list-disc list-inside space-y-1">
            {letter.disclaimers.map((d, i) => (
              <li key={i}>{d}</li>
            ))}
          </ul>
        </div>
        <details className="rounded-lg border border-slate-800 bg-slate-950/60 px-4 py-3 text-xs text-slate-300">
          <summary className="cursor-pointer text-slate-200 font-semibold">
            Rechtsgrundlage ({letter.legalBasis.length})
          </summary>
          <ul className="mt-2 list-disc list-inside space-y-1">
            {letter.legalBasis.map((b, i) => (
              <li key={i}>{b}</li>
            ))}
          </ul>
        </details>
      </div>
    </div>
  );
}
